// components/Hero.tsx
"use client";

import Link from 'next/link';
import { usePathname } from 'next/navigation';
import dynamic from 'next/dynamic';
import { FaStar, FaHeadset, FaShieldAlt, FaCar, FaMotorcycle, FaTruck, FaPaw } from 'react-icons/fa';

// Dynamically import EnquiryForm with SSR disabled
const EnquiryForm = dynamic(
  () => import('./EnquiryForm'),
  { ssr: false, loading: () => <div className="p-4 text-center text-sm">Loading form...</div> }
);

interface HeroProps {
  title?: string;
  subtitle?: string;
  trustBadgeText?: string;
  rating?: number;
  reviewCount?: number;
  phoneNumber?: string;
  backgroundImage?: string;
}

const DEFAULT_IMAGE = '/images/movezze-hero-default.jpg';

const serviceTabs = [
  { id: 'car', label: 'Car Transport', href: '/car-transport', icon: FaCar },
  { id: 'bike', label: 'Bike Shifting', href: '/bike-shifting', icon: FaMotorcycle },
  { id: 'household', label: 'Packers & Movers', href: '/packers-movers', icon: FaTruck },
  { id: 'pet', label: 'Pet Relocation', href: '/pet-relocation', icon: FaPaw },
];

export default function Hero({
  title = "Move Anything, Anywhere in India",
  subtitle = "Trusted packers and movers for household shifting, bike transport, car carrier, and pet relocation across 100+ cities.",
  trustBadgeText = "Trusted by 1,000+ Happy Customers",
  rating = 4.9,
  reviewCount = 2500,
  phoneNumber = "+91 8130 880 900",
  backgroundImage = DEFAULT_IMAGE,
}: HeroProps) {
  const pathname = usePathname();

  const getActiveService = () => {
    for (const tab of serviceTabs) {
      if (pathname === tab.href || pathname?.startsWith(tab.href + '/')) {
        return tab.id;
      }
    }
    return 'car';
  };

  const activeService = getActiveService();

  // On homepage, show "Select Service" (empty). On other pages, auto‑select.
  const formInitialService = pathname === '/' ? '' : activeService;

  return (
    <section className="relative max-h-[400px] md:min-h-[600px] flex lg:items-center">
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: `url(${backgroundImage})` }}
      />
      <div className="absolute inset-0 bg-black/60 block md:hidden"></div>
      <div className="absolute inset-0 bg-black/70 hidden md:block"></div>

      <div className="relative z-10 max-w-6xl mx-auto px-4 py-10 md:py-16 w-full">
        <div className="flex flex-col items-center text-center">

          <h1 className="text-2xl md:text-3xl lg:text-4xl font-black text-white leading-[1.1] mb-4 tracking-tight">
            {title}
          </h1>
          <p className="text-white/90 text-base md:text-lg max-w-3xl mx-auto mb-6">
            {subtitle}
          </p>

          {/* ===== CARD ===== */}
          <div className="mt-4 w-full max-w-5xl mx-auto rounded-xl shadow-2xl overflow-hidden">

            <div className="bg-white px-6 py-3 text-center border-b border-white/10">
              <h2 className="text-[#292929] font-bold text-sm md:text-base tracking-wide">
                ✅ Get a Free Quote – Transparent Pricing
              </h2>
            </div>

            {/* Card Body */}
            <div className="p-5 md:p-6 bg-white/90 backdrop-blur-xs">

              {/* Tabs – underline style */}
              <div className="flex flex-wrap gap-6 border-b border-gray-300 mb-5">
                {serviceTabs.map((tab) => {
                  const Icon = tab.icon;
                  const isActive = tab.id === activeService;
                  return (
                    <Link
                      key={tab.id}
                      href={tab.href}
                      className={`
                        flex items-center gap-2 pb-3 text-sm font-medium transition-colors
                        border-b-2 -mb-[3px]
                        ${isActive
                          ? 'border-[#f62b1e] text-[#f62b1e]'
                          : 'border-transparent text-[#343434] hover:text-[#f62b1e] hover:border-[#f62b1e]'
                        }
                      `}
                    >
                      <Icon className="text-base" />
                      <span className="hidden sm:inline">{tab.label}</span>
                      <span className="sm:hidden">{tab.label.split(' ')[0]}</span>
                    </Link>
                  );
                })}
              </div>

              {/* Form – dynamically imported to avoid SSR issues */}
              <EnquiryForm
                initialService={formInitialService}
                mode="horizontal"
                variant="light"
                showServiceDropdown={true}
              />

              {/* Footer */}
              <div className="text-center text-xs text-[#343434] mt-4 flex items-center justify-center gap-2">
                <FaShieldAlt className="text-green-500" />
                <span>100% Safe • Trusted • Live Tracking Update</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}